import React, {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  ModalIsland,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { AAG_SOD_REPORT } from '../../../shared/constants';
import {
  useInvertedLocalize,
  useLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../shared/hooks';
import { type RootState } from '../../../shared/types';
import { getFilterPayload } from '../../ViolationsTab/utils';
import { useLazyGetReportColumnsQuery } from '../data-layer/violationsReportApi';
import { REPORT_MODAL_TABS } from '../data-layer/violationsReportConstants';
import { violationsReportActions } from '../data-store/violationsReportSlice';
import useExportReport from '../hooks/use-exportReport';
import { buildColumnDisplay, getColumnConfig } from '../utils/columnConfig';

import CurrentView from './ReportView/CurrentView';
import MyReport from './ReportView/MyReport';
import Templates from './ReportView/Templates';

import './ViolationsReport.css';

interface ReportModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isViewAllReport?: boolean;
}

/**
 * ReportModal
 */
function ReportModal({
  isOpen,
  setIsOpen,
  isViewAllReport,
}: ReportModalProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const localize = useLocalize();

  const { navigateToUrl } = useNavigateTo();
  const { exportReport, isExporting } = useExportReport();

  // Note: Sagar to create a utility out of it
  const { activeFilters, tabInAction, searchKey, activeInsight } =
    useTypedSelector((state) => state.aag.violationsTab.controls);
  const activeFiltersMap = Object.fromEntries(
    activeFilters.map((f) => [
      f.value,
      f.options.filter((o) => o.isChecked).map((o) => o.value),
    ])
  );

  // Count only filters that have at least one selected option, excluding graph-only filters
  const appliedFiltersCount = useMemo(
    () =>
      Object.values(activeFiltersMap).reduce(
        (count, arr) =>
          Array.isArray(arr) && arr.length > 0 ? count + 1 : count,
        0
      ),
    [activeFiltersMap]
  );

  const baseFilters = getFilterPayload(
    activeFiltersMap,
    tabInAction ?? undefined,
    searchKey
  );

  const filters = {
    ...baseFilters,
    insightRequests:
      activeInsight !== null
        ? [
            {
              id: activeInsight.id,
              filters: activeInsight.filters,
            },
          ]
        : undefined,
  };

  const {
    selectedReport,
    exportProgress,
    exportStatusByReportType,
    activeExports,
    columnsByReportType,
  } = useTypedSelector((state: RootState) => state.aag.report);

  const [triggerGetReportColumns] = useLazyGetReportColumnsQuery();

  const [pollPct, setPollPct] = useState(0);
  const [isExtendedLoading, setIsExtendedLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const successTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [activeTab, setActiveTab] = useState(
    isViewAllReport
      ? REPORT_MODAL_TABS.TEMPLATES
      : REPORT_MODAL_TABS.CURRENT_VIEW
  );
  const isEntitlementPage = window.location.pathname.includes('entitlement');

  // Define available report types for each tab
  const TEMPLATE_ONLY_REPORTS = [
    'entitmentsWithInherentRisks',
    'violationWithMitigatingControls',
  ];

  // Initialize with appropriate default based on page
  const getInitialReport = React.useCallback(() => {
    if (isEntitlementPage) {
      return 'entitmentsWithInherentRisks';
    }

    return selectedReport || 'summary';
  }, [isEntitlementPage, selectedReport]);

  const [localSelectedReport, setLocalSelectedReport] = useState(
    getInitialReport()
  );

  const shouldHideExport = useMemo(
    () => localSelectedReport === 'entitmentsWithInherentRisks',
    [localSelectedReport]
  );

  // Map reportValue to API reportType
  const getReportType = (
    reportValue: string
  ): 'SUMMARY' | 'DETAIL' | 'PERMISSIONS' | null => {
    const upper = reportValue.toUpperCase();

    if (upper === 'SUMMARY' || upper === 'DETAIL' || upper === 'PERMISSIONS') {
      return upper;
    }

    return null;
  };

  // Fetch columns for a report type if not cached
  const fetchColumnsIfNeeded = React.useCallback(
    (reportValue: string): void => {
      const reportType = getReportType(reportValue);

      if (!reportType) {
        return;
      }

      // Check if already cached
      const cached = columnsByReportType?.[reportType];

      if (cached) {
        return;
      }

      // Fetch silently in background
      triggerGetReportColumns(reportType)
        .unwrap()
        .then((response) => {
          dispatch(
            violationsReportActions.setColumnsForReportType({
              reportType,
              columns: response,
            })
          );
        })
        .catch(() => {
          // Silent failure - fallback to cached data if available
        });
    },
    [columnsByReportType, dispatch, triggerGetReportColumns]
  );

  // Fetch columns for default report (summary) on modal open
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const initialReport = getInitialReport();

    // Only fetch for summary/detail/permissions report types
    if (['summary', 'detail', 'permissions'].includes(initialReport)) {
      fetchColumnsIfNeeded(initialReport);
    }
  }, [isOpen, getInitialReport, fetchColumnsIfNeeded]);

  const handleChange = (value: string) => {
    setActiveTab(value);
    dispatch(violationsReportActions.setReportModalTab(value));

    // Handle report selection sync when switching tabs
    if (value === REPORT_MODAL_TABS.CURRENT_VIEW) {
      // If switching to Current View and current selection is template-only, fallback to summary
      if (TEMPLATE_ONLY_REPORTS.includes(localSelectedReport)) {
        const fallbackReport = 'summary';

        setLocalSelectedReport(fallbackReport);
        dispatch(violationsReportActions.setSelectedReport(fallbackReport));
      }
    }
  };

  // Sync local progress with API progress
  useEffect(() => {
    const currentReport = localSelectedReport || selectedReport;

    if (currentReport === 'entitmentsWithInherentRisks') {
      return;
    }

    const currentReportKey = currentReport.toUpperCase();
    const apiProgress = exportProgress[currentReportKey] || 0;

    setPollPct(apiProgress);

    setIsExtendedLoading(apiProgress > 0 && apiProgress < 100);
  }, [exportProgress, localSelectedReport, selectedReport]);

  // Stop polling when reaching 100 and mark success.
  useEffect(() => {
    if (!isExtendedLoading) {
      return;
    }

    if (pollPct < 100) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsExtendedLoading(false);
  }, [isExtendedLoading, pollPct]);

  // Auto-reset success after 5 seconds and clear export progress
  useEffect(() => {
    const currentReport = localSelectedReport || selectedReport;

    if (currentReport === 'entitmentsWithInherentRisks') {
      return;
    }

    const currentReportKey = currentReport.toUpperCase();
    const currentProgressValue = exportProgress[currentReportKey] || 0;

    if (currentProgressValue >= 100) {
      successTimerRef.current = setTimeout(() => {
        dispatch(
          violationsReportActions.setExportProgress({
            reportType: currentReportKey,
            progressPercentage: 0,
          })
        );
      }, 3000);
    }
  }, [exportProgress, localSelectedReport, selectedReport, dispatch]);

  const handleReportSelect = (reportValue: string) => {
    setLocalSelectedReport(reportValue);
    // Don't update Redux state immediately - only update when Preview is clicked
    // Fetch columns for the selected report type
    fetchColumnsIfNeeded(reportValue);
  };

  const body = () => {
    switch (activeTab) {
      case REPORT_MODAL_TABS.CURRENT_VIEW:
        return (
          <CurrentView
            onReportSelect={handleReportSelect}
            selectedReport={localSelectedReport}
            appliedFiltersCount={appliedFiltersCount}
          />
        );
      case REPORT_MODAL_TABS.TEMPLATES:
        return (
          <Templates
            onReportSelect={handleReportSelect}
            selectedReport={localSelectedReport}
          />
        );
      case REPORT_MODAL_TABS.MY_REPORT:
        return <MyReport />;
      default:
        return <Box />;
    }
  };

  const handleOnClose = () => {
    setIsOpen(false);
  };

  const headerComp = () => (
    <Box tag='header' className='ReportModal-headerWrapper'>
      <Box className='ReportModal-header'>
        <Icon kind='Report' />
        <Box className='ModalIsland-titleContainer'>
          <Typography kind='h2' className='ModalIsland-title'>
            {l('Report')}
          </Typography>
        </Box>
      </Box>
      <Box tag='div' className='ModalIsland-closeBtn'>
        <ButtonIcon
          id='modalCloseButton-reportModal'
          dataTestId='ModalIslandCloseButton'
          size='medium'
          kind='ghost'
          ariaLabel={l('Close')}
          icon={<Icon kind='Close' color='neutral-100' size='smallMedium' />}
          onClick={handleOnClose}
        />
      </Box>
    </Box>
  );

  const headerComponent = () => (
    <Box className='ReportModal-headerComp'>
      {headerComp()}
      <TabGroup
        value={activeTab}
        onChange={handleChange}
        className='ReportModal-TabGroup'>
        {isViewAllReport ? null : (
          <TabItem
            label={l('Current View')}
            value={REPORT_MODAL_TABS.CURRENT_VIEW}
          />
        )}
        <TabItem label={l('Templates')} value={REPORT_MODAL_TABS.TEMPLATES} />
        {/* Once this is supported uncomment here */}
        {/* <TabItem label={l('My Report')} value={REPORT_MODAL_TABS.MY_REPORT} /> */}
      </TabGroup>
    </Box>
  );

  const handlePreviewclick = () => {
    // Use local selected report if available, otherwise fall back to selectedReport
    const reportToUse = localSelectedReport || selectedReport;

    // Update Redux state only when Preview is clicked
    dispatch(violationsReportActions.setSelectedReport(reportToUse));
    dispatch(violationsReportActions.setReportModalTab(activeTab));

    // Force preview reload when coming from template mode
    if (activeTab === REPORT_MODAL_TABS.TEMPLATES) {
      dispatch(violationsReportActions.setReportError({ state: false }));
    }

    setIsOpen(false);

    // Navigate to the correct page based on report type
    const reportUrl =
      reportToUse === 'entitmentsWithInherentRisks'
        ? AAG_SOD_REPORT.replace('/violations/', '/entitlement/')
        : AAG_SOD_REPORT;

    navigateToUrl(reportUrl);
  };

  // Calculate export button properties
  const exportButtonProps = useMemo(() => {
    if (
      !localSelectedReport ||
      localSelectedReport === 'entitmentsWithInherentRisks'
    ) {
      return {
        currentProgress: 0,
        currentProgressRaw: 0,
        showProgressButton: false,
      };
    }

    const currentReportKey = localSelectedReport.toUpperCase();
    const currentProgress = exportProgress[currentReportKey] || 0;
    const currentProgressRaw = exportProgress[currentReportKey] || 0;
    const currentHasActiveExport =
      Array.isArray(activeExports) &&
      activeExports.some(
        (e: { reportType: string }) => e.reportType === currentReportKey
      );

    return {
      currentProgress,
      currentProgressRaw,
      showProgressButton:
        currentHasActiveExport ||
        (currentProgress > 0 &&
          exportStatusByReportType?.[currentReportKey] !== 'FAILED'),
    };
  }, [
    localSelectedReport,
    exportProgress,
    activeExports,
    exportStatusByReportType,
  ]);

  const handleExport = async () => {
    // Use local selected report if available, otherwise fall back to selectedReport
    const reportToUse = localSelectedReport || selectedReport;

    // Dispatch the selected report before export
    if (localSelectedReport) {
      dispatch(violationsReportActions.setSelectedReport(localSelectedReport));
    }

    const reportTypeUpper = reportToUse.toUpperCase() as
      | 'DETAIL'
      | 'SUMMARY'
      | 'PERMISSIONS';

    const columnConfig = getColumnConfig(reportTypeUpper);
    const columnsData = columnsByReportType?.[reportTypeUpper];
    const columnDisplay = buildColumnDisplay(
      columnConfig.selectedColumns,
      columnsData,
      localize
    );

    const payload = {
      reportType: reportTypeUpper,
      format: 'EXCEL' as const,
      locale: 'en' as const,
      columnConfig: {
        ...columnConfig,
        columnDisplay,
      },
      filters,
    };

    await exportReport(payload);
  };

  const renderFooter = () => {
    const currentReportKey =
      (localSelectedReport || selectedReport).toUpperCase() || '';

    return (
      <FooterActionBar size='large'>
        <Box className='ReportModal-footer' style={{ overflow: 'visible' }}>
          <Button kind='subtle' size='medium' onClick={handleOnClose}>
            {l('Cancel')}
          </Button>
          <div className='ReportModal-footer--primary'>
            {!shouldHideExport &&
              (exportButtonProps.currentProgressRaw > 0 &&
              exportButtonProps.currentProgress > 0 &&
              exportButtonProps.currentProgress < 100 &&
              exportButtonProps.showProgressButton ? (
                <Tooltip
                  text={l(
                    'An export is already in progress for this report type'
                  )}
                  placement='top'
                  isPortalEnabled
                  trigger={
                    <div style={{ display: 'inline-block' }}>
                      <Button
                        key={`export-progress-${currentReportKey}`}
                        kind={
                          exportButtonProps.currentProgress > 0
                            ? 'filled'
                            : 'outlined'
                        }
                        size='medium'
                        isExtendedLoading={
                          exportButtonProps.currentProgress > 0 &&
                          exportButtonProps.currentProgress < 100
                        }
                        isSuccess={exportButtonProps.currentProgress >= 100}
                        extendedLoadingPercentValue={
                          exportButtonProps.currentProgress
                        }
                        onClick={handleExport}
                        loaderText={`${l('Exporting')} (${
                          exportButtonProps.currentProgressRaw
                        }%)`}
                        successText={l('Exported')}
                        className={cx(
                          exportButtonProps.currentProgress > 0 &&
                            exportButtonProps.currentProgress < 100 &&
                            'Sod-Report-Exporting-button Button--isExtendedLoading'
                        )}>
                        {l('Export')}
                      </Button>
                    </div>
                  }
                />
              ) : (
                <Button
                  key={`export-progress-${currentReportKey}`}
                  kind='outlined'
                  size='medium'
                  onClick={handleExport}
                  isDisabled={isExporting}>
                  {isExporting ? l('Exporting...') : l('Export')}
                </Button>
              ))}
            <Button kind='filled' size='medium' onClick={handlePreviewclick}>
              {l('Preview')}
            </Button>
          </div>
        </Box>
      </FooterActionBar>
    );
  };

  return (
    <ModalIsland
      onClose={handleOnClose}
      isOpen={isOpen}
      HeaderComp={headerComponent()}
      contentBody={
        <Box className='ReportModal-body'>
          {/* ExportBanner rendered at page level to avoid duplicates */}
          {body()}
        </Box>
      }
      FooterActionBarComp={renderFooter()}
      className='ReportModal Sod-ReportModal'
    />
  );
}

ReportModal.defaultProps = {
  isViewAllReport: false,
};

export default ReportModal;
